import { ChevronRight, LocateFixed, Search } from 'lucide-react';
import { useCallback, useState, type FormEvent } from 'react';
import { usePolling } from '../../hooks/usePolling';
import { endpoints } from '../../services/api';
import { startMicrosoft, useSession } from '../../store/session';
import { currentPosition, searchPlaces } from '../../services/weather';
import { useSettings, type Location } from '../../store/settings';
import { buttonClass, Choice, ghostButtonClass, inputClass, Section, Switch, Toggle, WidgetPage } from './controls';
import { WIDGET_PAGES, type WidgetPageId } from './widgetPages';

const ZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
})();

/** Settings → Widgets: Photos only, and every widget with a quick on/off and a link to its page. */
export function WidgetsOverview({ go }: { go: (tab: string) => void }) {
  const s = useSettings();
  const update = s.update;
  const VIEW = { list: 'List', day: 'Day', week: 'Week', month: 'Month' } as const;
  const summary: Record<WidgetPageId, string> = {
    clock: `${s.clock.hour12 ? '12' : '24'}-hour${s.clock.timeZone ? ` · ${s.clock.timeZone.replace(/_/g, ' ')}` : ''}`,
    location: `Where & when each photo was taken${s.photoInfo.showMap ? ', with a map' : ''}`,
    weather: s.weather.location?.name ?? 'No location set',
    calendar: `${VIEW[s.calendar.view]} view`,
    lists: s.lists.source === 'microsoft' ? 'Microsoft To Do' : 'KCL Canvas lists',
    news: { ticker: 'Ticker', card: 'Story card', both: 'Ticker + story card' }[s.news.display],
  };

  return (
    <>
      <Section
        title="Photos only"
        description="Show just your photos and videos – no clock, location, weather, calendar, lists or news. Your widget settings are kept for when you switch back."
      >
        <Toggle label="Photos only" checked={s.photosOnly} onChange={(v) => update('photosOnly', v)} />
      </Section>
      <Section title="Widgets" description={s.photosOnly ? 'Hidden while Photos only is on.' : 'Switch each one on or off, or open it for its options.'}>
        <ul className="space-y-2">
          {WIDGET_PAGES.map((w) => (
            <li key={w.id} className="flex items-center gap-3 rounded-xl border border-[var(--border)] p-3">
              <w.icon className="h-5 w-5 shrink-0 opacity-80" aria-hidden />
              <button type="button" className="min-w-0 flex-1 text-left" onClick={() => go(w.id)}>
                <div className="text-sm font-medium">{w.name}</div>
                <div className="muted truncate text-xs">{s[w.key].enabled ? summary[w.id] : 'Off'}</div>
              </button>
              <Switch label={`Show ${w.name}`} checked={s[w.key].enabled} onChange={(enabled) => update(w.key, { enabled })} />
              <button
                type="button"
                onClick={() => go(w.id)}
                aria-label={`${w.name} options`}
                className="rounded-lg p-1.5 hover:bg-[var(--border)]"
              >
                <ChevronRight className="h-5 w-5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
}

export function ClockTab() {
  const clock = useSettings((s) => s.clock);
  const update = useSettings((s) => s.update);
  return (
    <WidgetPage label="Show clock" enabled={clock.enabled} onChange={(enabled) => update('clock', { enabled })}>
      <Section title="Format">
        <Toggle label="12-hour clock" checked={clock.hour12} onChange={(hour12) => update('clock', { hour12 })} />
        <Toggle label="Show seconds" checked={clock.showSeconds} onChange={(showSeconds) => update('clock', { showSeconds })} />
        <Toggle label="Show date" checked={clock.showDate} onChange={(showDate) => update('clock', { showDate })} />
        <label className="block text-sm">
          <span className="mb-1 block">Time zone</span>
          <select
            className={`${inputClass} bg-[var(--surface-strong)]`}
            value={clock.timeZone}
            onChange={(e) => update('clock', { timeZone: e.target.value })}
          >
            <option value="">This device ({Intl.DateTimeFormat().resolvedOptions().timeZone})</option>
            {ZONES.map((z) => (
              <option key={z} value={z}>
                {z.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </label>
      </Section>
    </WidgetPage>
  );
}

export function WeatherTab() {
  const weather = useSettings((s) => s.weather);
  const update = useSettings((s) => s.update);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Location[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async (e: FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await searchPlaces(query.trim());
      setResults(r);
      if (!r.length) setError('No places found.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setBusy(false);
    }
  };

  const locate = async () => {
    setBusy(true);
    setError(null);
    try {
      update('weather', { location: await currentPosition() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not get location');
    } finally {
      setBusy(false);
    }
  };

  return (
    <WidgetPage
      label="Show weather"
      description="7-day forecast from Open-Meteo."
      enabled={weather.enabled}
      onChange={(enabled) => update('weather', { enabled })}
    >
      <Section title="Location">
        <p className="text-sm">
          Location: <strong>{weather.location?.name ?? 'not set'}</strong>
        </p>
        <form onSubmit={search} className="flex gap-2">
          <input
            className={inputClass}
            placeholder="Search a town or city"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search location"
          />
          <button className={buttonClass} disabled={busy} type="submit" aria-label="Search">
            <Search className="h-4 w-4" aria-hidden />
          </button>
          <button className={ghostButtonClass} disabled={busy} type="button" onClick={locate} aria-label="Use my location">
            <LocateFixed className="h-4 w-4" aria-hidden />
          </button>
        </form>
        {error && <p className="text-sm text-red-400">{error}</p>}
        {results.length > 0 && (
          <ul className="space-y-1">
            {results.map((r) => (
              <li key={`${r.latitude},${r.longitude}`}>
                <button
                  className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-[var(--border)]"
                  onClick={() => {
                    update('weather', { location: r });
                    setResults([]);
                    setQuery('');
                  }}
                >
                  {r.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Show">
        <Toggle
          label="Fahrenheit"
          checked={weather.unit === 'fahrenheit'}
          onChange={(f) => update('weather', { unit: f ? 'fahrenheit' : 'celsius' })}
        />
        <Toggle label="Hourly forecast" checked={weather.showHourly} onChange={(showHourly) => update('weather', { showHourly })} />
        <Toggle label="7-day forecast" checked={weather.showDaily} onChange={(showDaily) => update('weather', { showDaily })} />
        <Toggle
          label="Weather warnings (official, via MeteoAlarm – Europe)"
          checked={weather.showWarnings}
          onChange={(showWarnings) => update('weather', { showWarnings })}
        />
        {weather.showWarnings && weather.location && !weather.location.countryCode && (
          <p className="muted text-xs">Warnings need a searched location – search for your town above.</p>
        )}
        <Toggle label="Feels-like temperature" checked={weather.showFeelsLike} onChange={(showFeelsLike) => update('weather', { showFeelsLike })} />
        <Toggle label="Chance of rain" checked={weather.showRain} onChange={(showRain) => update('weather', { showRain })} />
        <Toggle label="Wind" checked={weather.showWind} onChange={(showWind) => update('weather', { showWind })} />
        {weather.showWind && (
          <Choice
            options={[
              { id: 'kmh', name: 'km/h' },
              { id: 'mph', name: 'mph' },
              { id: 'ms', name: 'm/s' },
            ]}
            value={weather.windUnit}
            onChange={(windUnit) => update('weather', { windUnit })}
            columns={3}
          />
        )}
        <Toggle label="Humidity" checked={weather.showHumidity} onChange={(showHumidity) => update('weather', { showHumidity })} />
        <Toggle label="UV index" checked={weather.showUv} onChange={(showUv) => update('weather', { showUv })} />
        <Toggle label="Sunrise & sunset" checked={weather.showSun} onChange={(showSun) => update('weather', { showSun })} />
      </Section>
    </WidgetPage>
  );
}

/** Which Microsoft To Do lists appear as tabs in the widget. */
function TodoListChooser() {
  const hidden = useSettings((s) => s.lists.microsoftHidden);
  const update = useSettings((s) => s.update);
  const { data, loading, error } = usePolling('todo-lists-settings', useCallback(() => endpoints.todoLists().then((r) => r.lists), []), 10 * 60_000);
  if (!data) return <p className="muted text-xs">{loading ? 'Loading your To Do lists…' : error}</p>;
  return (
    <ul className="space-y-1.5 pl-1">
      {data.map((l) => (
        <li key={l.id}>
          <label className="flex cursor-pointer items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={!hidden.includes(l.id)}
              onChange={() =>
                update('lists', { microsoftHidden: hidden.includes(l.id) ? hidden.filter((h) => h !== l.id) : [...hidden, l.id] })
              }
              className="accent-[var(--accent)]"
            />
            {l.name}
          </label>
        </li>
      ))}
    </ul>
  );
}

export function ListsTab() {
  const { config, microsoft } = useSession();
  const lists = useSettings((s) => s.lists);
  const update = useSettings((s) => s.update);
  return (
    <WidgetPage label="Show lists" enabled={lists.enabled} onChange={(enabled) => update('lists', { enabled })}>
      <Section title="Lists">
        {config?.features.microsoft && (
          <Choice
            options={[
              { id: 'local', name: 'KCL Canvas lists', description: 'To-do + shopping, synced between screens' },
              { id: 'microsoft', name: 'Microsoft To Do', description: 'Your lists from the linked account' },
            ]}
            value={lists.source}
            onChange={(source) => update('lists', { source })}
          />
        )}
        {lists.source === 'microsoft' && config?.features.microsoft ? (
          microsoft?.connected && microsoft.tasks ? (
            <TodoListChooser />
          ) : (
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="muted">
                {microsoft?.connected ? 'KCL Canvas needs permission to read your To Do lists.' : 'Connect your Microsoft account first.'}
              </span>
              <button className="shrink-0 text-[var(--accent)] hover:underline" onClick={() => startMicrosoft('link')}>
                {microsoft?.connected ? 'Reconnect Microsoft' : 'Connect Microsoft'}
              </button>
            </div>
          )
        ) : (
          <p className="muted text-xs">Lists sync between screens when you’re signed in.</p>
        )}
      </Section>
    </WidgetPage>
  );
}
